import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconInfoCircle, IconPencil, IconPlus, IconTrash, IconUpload } from "@tabler/icons-react"

interface ExchangeRate {
  id: number
  date: string
  currency_from: string
  currency_to: string
  currency_pair: string
  buy_rate: string
  sell_rate: string
  source: string
}

interface LatestRate {
  date: string
  buy_rate: string
  sell_rate: string
}

interface Props {
  [key: string]: unknown
  exchange_rates: ExchangeRate[]
  pagy: PagyMeta
  latest_rate: LatestRate | null
  can_create: boolean
}

function sourceVariant(source: string) {
  switch (source) {
    case "api":
      return "default" as const
    case "csv":
      return "secondary" as const
    default:
      return "outline" as const
  }
}

function formatRate(rate: string) {
  return Number(rate).toFixed(4)
}

function handleDelete(id: number) {
  if (confirm("Delete this rate?")) {
    router.delete(`/exchange_rates/${id}`)
  }
}

export default function ExchangeRatesIndex() {
  const { exchange_rates, pagy, latest_rate, can_create } = usePage<Props>().props

  return (
    <>
      <Head title="Exchange Rates - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Exchange Rates</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Currency Exchange Rates</h1>
          {can_create && (
            <div className="flex gap-2">
              <Button size="sm" asChild>
                <a href="/exchange_rates/new">
                  <IconPlus className="mr-1 h-4 w-4" />
                  Add Rate
                </a>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href="/exchange_rates/import">
                  <IconUpload className="mr-1 h-4 w-4" />
                  Import CSV
                </a>
              </Button>
            </div>
          )}
        </div>

        {latest_rate && (
          <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/50">
            <IconInfoCircle className="h-5 w-5 shrink-0 text-blue-500" />
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Latest Rate ({formatDate(latest_rate.date)}):{" "}
              <span className="font-bold">Buy: {formatRate(latest_rate.buy_rate)}</span> |{" "}
              <span className="font-bold">Sell: {formatRate(latest_rate.sell_rate)}</span>
            </p>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Currency Pair</TableHead>
              <TableHead className="text-right">Buy Rate</TableHead>
              <TableHead className="text-right">Sell Rate</TableHead>
              <TableHead>Source</TableHead>
              {can_create && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {exchange_rates.map((rate) => (
              <TableRow key={rate.id}>
                <TableCell className="font-medium">{formatDate(rate.date)}</TableCell>
                <TableCell>{rate.currency_pair}</TableCell>
                <TableCell className="text-right font-semibold">{formatRate(rate.buy_rate)}</TableCell>
                <TableCell className="text-right font-semibold">{formatRate(rate.sell_rate)}</TableCell>
                <TableCell>
                  <Badge variant={sourceVariant(rate.source)}>
                    {rate.source.charAt(0).toUpperCase() + rate.source.slice(1)}
                  </Badge>
                </TableCell>
                {can_create && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" asChild>
                        <a href={`/exchange_rates/${rate.id}/edit`}>
                          <IconPencil className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(rate.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <IconTrash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {exchange_rates.length === 0 && (
              <TableRow>
                <TableCell colSpan={can_create ? 6 : 5} className="text-muted-foreground text-center">
                  No exchange rates found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

ExchangeRatesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
